import { Strategy, StrategyEstimated, StrategyResponseData } from '~/api-services/AIStrategyBuilderService/types';
import { convertBalances, formatStat, STRATEGY_NO_DATA } from '~/constants/strategyConstants';
import { formatNumberGrouped } from '~/lib/format';
import i18n from '~/i18n';

export type TradeRow = {
    index: number;
    openPrice?: number;
    closePrice?: number;
    // Profit in the instrument's quote currency, straight from the engine.
    result?: number;
    // The same profit in account currency, at the rate frozen when the strategy was created.
    // Undefined when the strategy carries no rate — then only `result` is shown.
    resultAccount?: number;
    openTime?: string;
    closeTime?: string;
};

/**
 * Everything the result surfaces need, read off one loaded strategy: the engine's own output,
 * the account-currency restatement of it, and the backend's computed summary (`backend_json`).
 *
 * The backend only fills `backend_json` when the strategy was created with `exchangeRates` in
 * its frontend_json, so all of its fields are nullable and every consumer must degrade to "—".
 */
export type StrategyResultView = {
    response?: StrategyResponseData;
    estimated?: StrategyEstimated;
    // Per-trade profit in account currency, index-aligned with `response.trades.balance`.
    accountBalances: number[] | null;
    instrumentCurrency: string;
    accountCurrency: string;
    requiredFunds?: number | null;
    resultInstrument?: number | null;
    resultAccount?: number | null;
    // Return on the minimum required balance — the headline number, replacing the engine's
    // estimated_strategy.percent_change (which was return on the submitted fund).
    resultPercentage?: number | null;
};

export function strategyResultView(strategy: Strategy | null | undefined): StrategyResultView {
    const response = (strategy?.response ?? undefined) as StrategyResponseData | undefined;
    const bj = strategy?.backend_json;
    return {
        response,
        estimated: response?.estimated_strategy,
        accountBalances: convertBalances(response?.trades?.balance, strategy?.frontend_json?.exchangeRates),
        instrumentCurrency: bj?.instrument_currency ?? '',
        accountCurrency: bj?.account_currency ?? '',
        requiredFunds: bj?.required_funds,
        resultInstrument: bj?.result_total_instrument_currency,
        resultAccount: bj?.result_total_account_currency,
        resultPercentage: bj?.result_percentage,
    };
}

// Append a currency code when one is known. Strategies with no backend_json render bare values,
// exactly as they did before the currency fields existed.
export const withCurrency = (text: string, currency?: string): string => (currency ? `${text} ${currency}` : text);

// The big number on the reveal and the decision panel: the backend's projected return, or "—"
// for strategies whose backend_json was never computed.
export const resultHeadline = (results: StrategyResultView): string =>
    typeof results.resultPercentage === 'number' ? `${formatNumberGrouped(results.resultPercentage, 2)}%` : '—';

// "12 trades · Best +19.52 USD · Worst -21.02 USD" — the supporting line under the headline.
// Best/worst come from the engine, so they stay in the instrument's quote currency.
export const resultSummaryLine = (results: StrategyResultView): string => {
    const { estimated, instrumentCurrency } = results;
    const trades = buildTradeRows(results.response).length;
    return [
        `${trades} ${i18n.t('ai-strategy-decision-trades')}`,
        `${i18n.t('ai-strategy-decision-best')} ${withCurrency(formatStat(estimated?.best_trade), instrumentCurrency)}`,
        `${i18n.t('ai-strategy-decision-worst')} ${withCurrency(
            formatStat(estimated?.worst_trade),
            instrumentCurrency
        )}`,
    ].join(' · ');
};

// The Statistics rows, in display order: estimated_strategy keys -> i18n label keys.
// `money` rows are amounts in the instrument's quote currency and get its code appended; the
// ratios and the trade count carry no unit. `percent_change` is deliberately absent: it is the
// engine's return on the submitted fund, which the backend's `result_percentage` (return on the
// minimum required balance) now supersedes as the headline — showing both invites the question
// of why one is ~0% and the other three figures.
export const STAT_FIELDS: { key: keyof StrategyEstimated; labelKey: string; money?: boolean }[] = [
    { key: 'clear_balance', labelKey: 'stat-clear-balance', money: true },
    { key: 'best_trade', labelKey: 'stat-best-trade', money: true },
    { key: 'worst_trade', labelKey: 'stat-worst-trade', money: true },
    { key: 'expectation', labelKey: 'stat-expectation', money: true },
    { key: 'median', labelKey: 'stat-median', money: true },
    { key: 'sharpe_ratio', labelKey: 'stat-sharpe-ratio' },
    { key: 'sortino_ratio', labelKey: 'stat-sortino-ratio' },
    { key: 'VaR', labelKey: 'stat-var', money: true },
    { key: 'std_dev', labelKey: 'stat-std-dev', money: true },
    { key: 'mad', labelKey: 'stat-mad', money: true },
    { key: 'number_of_pos', labelKey: 'stat-number-of-pos' },
];

// Zip the response's parallel per-trade arrays into rows. A real trade always carries a
// "YYYY-MM-DD HH:MM:SS" enter date; the engine's no-data sentinel puts -99999 (a number)
// there instead — those rows are skipped.
export function buildTradeRows(
    response: StrategyResponseData | undefined,
    accountBalances?: number[] | null
): TradeRow[] {
    const t = response?.trades;
    if (!t) {
        return [];
    }
    const pin = t.price_in_arr ?? [];
    const pout = t.price_out_arr ?? [];
    const bal = t.balance ?? [];
    const ein = t.enter_date_array ?? [];
    const eout = t.exit_date_array ?? [];
    const n = Math.max(pin.length, pout.length, bal.length, ein.length, eout.length);
    const rows: TradeRow[] = [];
    for (let i = 0; i < n; i++) {
        if (typeof ein[i] !== 'string' || !ein[i]) {
            continue;
        }
        rows.push({
            index: i,
            openPrice: pin[i],
            closePrice: pout[i],
            result: bal[i],
            resultAccount: accountBalances?.[i],
            openTime: ein[i],
            closeTime: eout[i],
        });
    }
    return rows;
}

// Cumulative running sum of per-trade balance — the "Cumulative Profit / Loss" curve.
// Built from real trades only, so a sentinel-only payload yields no chart. Plots account
// currency when the strategy carries a frozen rate, otherwise the engine's raw values —
// the caller captions the axis accordingly.
export function buildCumulative(tradeRows: TradeRow[]): number[] {
    if (tradeRows.length === 0) {
        return [];
    }
    const out: number[] = [];
    let sum = 0;
    for (const row of tradeRows) {
        const b = Number(row.resultAccount ?? row.result);
        sum += Number.isFinite(b) && b !== STRATEGY_NO_DATA ? b : 0;
        out.push(Number(sum.toFixed(2)));
    }
    return out;
}
